<template>
  <div :id="`uploadContainer-${name}`">
    <input
      type="hidden"
      :name="`${name}Validation`"
      :value="flattenMedia"
      v-validate="uploadRequired"
      :data-vv-as="label"
    />

    <div v-if="media.length >= 10">
      <label class="block font-light font-sans leading-snug mb-1 subpixel-antialiased text-xl text-black w-full">{{ label }}</label>
      <div class="cursor-not-allowed disabledDropzone">
        <button disabled class="dz-message">
          <span class="prefix">
            <doe-icon icon="attachment" />
          </span>
          <span>
            {{ button }}
          </span>
        </button>
      </div>
    </div>
    <doe-dropzone
      v-else
      :name="name"
      :label="label"
      :hint="hint"
      :fieldRequired="fieldRequired"
      :acceptedTypes="acceptedTypes"
      :button="button"
      @mediaCreate="attachMedia"
    />
    <doe-error
      v-if="errors.has(`${name}Validation`)"
      :description="ariaErrorDescID(`${name}Validation`)"
      >{{ errors.first(`${name}Validation`) }}</doe-error
    >
    <doe-hint v-if="hint" :hint="hint" />
    <doe-gallery :media="media" @mediaDelete="detachMedia" />
  </div>
</template>

<script>
import dynamicValues from "@/mixins/dynamicValues";
import DoeHint from "@/components/field/Hint";
import DoeError from "@/components/field/Error";
import DoeDropzone from "@/components/upload/Dropzone.vue";
import DoeGallery from "@/components/upload/Gallery.vue";
import DoeIcon from "@/components/icon/Icon";

export default {
  name: "DoeUpload",
  mixins: [dynamicValues],
  inject: {
    $validator: "$validator"
  },
  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      type: Array
    },
    button: {
      type: String
    },
    label: {
      type: String,
      required: true
    },
    fieldRequired: {
      type: Boolean,
      default: false
    },
    validation: {
      required: false
    },
    hint: {
      type: [Array, String]
    },
    acceptedTypes: {
      type: String
    }
  },
  components: {
    DoeHint,
    DoeError,
    DoeDropzone,
    DoeGallery,
    DoeIcon
  },
  data() {
    return {
      media: []
    };
  },
  mounted() {
    if (this.value) {
      this.media = this.value;
    }
  },
  methods: {
    downgradeImage(base64) {
      return new Promise(function(resolve, reject) {
        const MAX_LENGTH = 1024;
        const QUALITY = 0.33;
        var canvas = document.createElement("canvas");
        var ctx = canvas.getContext("2d");
        var width;
        var height;
        var image = new Image();
        image.src = base64;

        image.onload = () => {

          if(image.width > MAX_LENGTH || image.height > MAX_LENGTH) {
            if (image.width > image.height) {
              width = MAX_LENGTH;
              height = image.height / (image.width / MAX_LENGTH);
            } else if (image.width < image.height) {
              width = image.width / (image.height / MAX_LENGTH);
              height = MAX_LENGTH;
            } else {
              width = MAX_LENGTH;
              height = MAX_LENGTH;
            }
          } else {
            width = image.width;
            height = image.height;
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(image, 0, 0,width, height);
          var lowQuality = canvas.toDataURL('image/jpeg', QUALITY);
          resolve(lowQuality)
        };
        image.onerror = () => reject(new Error(`Error for ${base64}`));
      });
    },
    isImage(fileType) {
      //.jpg, .jpeg, .png
      return fileType.includes("image");
    },
    calculateSize(base64) {
      let imageString = base64.replace('data:image/jpeg;base64,','');
      let n = imageString.length;
      let y = imageString.slice(-2) === '==' ? 2 : 1;
      return (n * (3/4)) - y
    },
    attachMedia(event) {
      if (this.isImage(event.type)) {
        let promise = this.downgradeImage(event.url);
        promise.then(
          base64 => {
            event.name = event.name.replace(".png", ".jpg");
            event.url = base64;
            event.type = 'image/jpeg';
            event.size = this.calculateSize(base64);
            this.media.push(event);
            this.$emit("input", this.media);
            this.$nextTick(function() {
              this.$validator.validate(`${this.name}Validation`);
            });
          },
          error => alert(`Error: ${error.message}`)
        );
      } else {
        this.media.push(event);
        this.$emit("input", this.media);
        this.$nextTick(function() {
          this.$validator.validate(`${this.name}Validation`);
        });
      }
    },
    detachMedia(event) {
      let index = this.media.findIndex(item => item.uuid === event.uuid);
      this.media.splice(index, 1);
      this.$emit("input", this.media);
      this.$nextTick(function() {
        this.$validator.validate(`${this.name}Validation`);
      });
    },
    ariaErrorDescID(fieldName) {
      return `${fieldName}ErrorDescription`;
    }
  },
  computed: {
    formatHint() {
      if (!Array.isArray(this.hint)) {
        return new Array(this.hint);
      } else {
        return this.hint;
      }
    },
    combinedMedia() {
      return [...this.media, ...this.value];
    },
    uploadRequired() {
      return this.getRequiredValue(this.validation);
    },
    flattenMedia() {
      if (this.media.length) {
        return JSON.stringify(this.media);
      } else {
        return "";
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.disabledDropzone {
  display: inline-block;
  width: 100%;
  max-width: 400px;
  height: 95px;
  border-radius: 4px;
  border: 2px dashed #b3b3b3;
  background-color: #fafafa;
  position: relative;
  z-index: 1;
}
span {
  @apply align-middle;
}
button.dz-message {
  @apply align-middle block;
  @apply cursor-not-allowed text-gray-700 font-sans font-semibold;
  @apply py-2 px-2 rounded border-2;
  @apply text-center text-gray-700 bg-gray-400 border-gray-700;
  @apply leading-snug py-2 text-xl;
  @apply mx-auto my-4;
  @apply w-10;
  &:focus {
    outline: 1px solid #041e42;
    outline-offset: 3px;
  }
  svg {
    @apply fill-current;
  }
}
span.prefix {
  @apply inline-block mr-1;
}
</style>
